import React from 'react'
import { Dropdown } from 'react-bootstrap'
import { Link } from 'react-router-dom'

import type { Notification } from '@ztechapps/common.contract'
import { timeFromNow, truncate } from '@ztechapps/common.utils'
import { BusyButtonLink } from '@ztechapps/webui.button'
import { HorizontalStack, TooltipPopOver, Scrollable } from '@ztechapps/webui.util'
import { RichTextDisplay } from '@ztechapps/webui.input'

import styles from './NotificationDropdown.module.scss'

export type NotificationDropdownItemProps = {
  notification: Notification
  loading?: boolean
  onToggleRead: (notification: Notification) => Promise<void>
}

export const NotificationDropdownItem = ({ notification, loading, onToggleRead }: NotificationDropdownItemProps) => {
  const { title, bodyPlain, bodyHtml, createdAt, isRead, url } = notification

  const handleToggleRead = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    await onToggleRead(notification)
  }

  const tooltipContent = (
    <Scrollable height="400px">
      <div className="notification-tooltip-body">
        <div className="fw-bold mb-2">{title}</div>
        {/* trustedSource: notification bodies are system-generated from our own templates, not attacker-reachable */}
        <RichTextDisplay contentHtml={bodyHtml ?? bodyPlain} className="p-1" trustedSource />
      </div>
    </Scrollable>
  )

  const content = (
    <HorizontalStack>
      <div className="me-2 flex-shrink-0 align-self-start">
        <BusyButtonLink
          icon={isRead ? 'envelope-open' : 'envelope'}
          onClick={handleToggleRead}
          disabled={loading}
          isBusy={loading}
          busyText=""
          // Unread passes no bsStyle so the button keeps `.btn-link`'s theme-aware colour; a
          // `text-primary` utility would pin it to the fill token and stay dark-blue in dark mode.
          bsStyle={isRead ? 'secondary' : undefined}
        />
      </div>
      <div className={styles.contentWrapper}>
        <HorizontalStack className="justify-content-between align-items-start mb-1">
          <span className={`${isRead ? 'text-body-secondary' : 'fw-bold text-body'} ${styles.titleText}`}>
            {truncate(title, 40)}
          </span>
          <small className="text-body-tertiary text-nowrap flex-shrink-0 ms-2">{timeFromNow(createdAt)}</small>
        </HorizontalStack>
        <TooltipPopOver placement="left" flip tooltipBody={tooltipContent} width="300px">
          <small className={`text-body-secondary ${styles.bodyPreview}`}>{bodyPlain}</small>
        </TooltipPopOver>
      </div>
    </HorizontalStack>
  )

  if (url) {
    return (
      <Dropdown.Item as={Link} to={url} className={`py-2 ${styles.notificationItem}`}>
        {content}
      </Dropdown.Item>
    )
  }

  return <Dropdown.Item className={`py-2 ${styles.notificationItem} ${styles.noLink}`}>{content}</Dropdown.Item>
}
